/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  useState
} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import ChevronRight from '@material-ui/icons/ChevronRight';
import isNull from 'lodash.isnull';
import {Moment} from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Account,
  GenericObject,
  User
} from '@ion/core/src/types/entities';
import {
  DateRangeType
} from '@ion/core/src/types/services';
import DateFilter from '@ion/dls/src/components/filters/DateFilter';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {
  DateFilterTitle,
  RadioMapType
} from '@ion/dls/src/components/menus/DateFilterMegaMenu';
import {getDateRangeFromValue} from '@ion/dls/src/components/menus/MenuUtils';
import NotificationTypeChooser from '@ion/dls/src/editors/NotificationTypeChooser';
import {PrimaryButton} from '../components/buttons/PrimaryButton';
import {TertiaryButton} from '../components/buttons/TertiaryButton';
import {ButtonSpinner} from '../components/loading/ButtonSpinner';
import styles, {getClasses} from '../editors/editorStyles';
import {
  useExtendStyles
} from '../hooks/useExtendStyles';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import {dlsColor} from '../theme/theme';
import {DateSelectorValues, IconSize} from '../types/types';

export interface NotificationFiltersProps {
  open: boolean;
  onCancel?: () => void;
  user?: User | GenericObject;
  onSubmit?: Function;
  account: Account;
  loading?: boolean;
  selectedFilter?: any;
}

/**
   *
   * This component provides the filter feature for the notification panel
   *
   * * ```typescript
   *
   *    import {NotificationFilters} from '@ion/dls/src/editors/';
   *
   *    <NotificationFilters open={editorOpen}
   *      account={account}
   *      loading={loading}
   *      onSubmit={onSubmit}
   *      onCancel={function() {
   *        setNotificationFilter(false);
   *      }} />
   *
   * ```
   *
   */
export const NotificationFilters = ({
  open,
  account,
  onSubmit,
  onCancel,
  selectedFilter,
  loading = false
}: NotificationFiltersProps) => {
  const editorStyles                                = useExtendStyles(styles);
  const classes                                     = editorStyles();
  const [customDateMode, setCustomDateMode]         = useState(selectedFilter?.selectedRadio === 'custom');
  const [selectedRange, setSelectedRange]           = useState<DateRangeType>(selectedFilter?.dateRange || null);
  const [selectedDate, setSelectedDate]             = useState<Array<Moment>>(null);
  const [dateError, setDateError]                   = useState<boolean>(false);
  const [notificationType, setNotificationType]     = useState(selectedFilter?.notificationType || '');
  const [selectedRadio, setSelectedRadio]           = useState<DateSelectorValues>(
    selectedFilter?.selectedRadio || DateSelectorValues.LastSeven);
  const isCustomValue = (value) => value === DateSelectorValues.Custom;

  function onRadioChange(event: ChangeEvent<HTMLInputElement>) {
    const {value} = event.target as HTMLInputElement;
    setSelectedRadio(value as DateSelectorValues);
    if (isCustomValue(value)) {
      setCustomDateMode(true);
    }
  }

  const radioMap: RadioMapType = {
    [DateSelectorValues.Live] : {
      label         : translate('common:time.live'),
      daysFromToday : null
    },
    [DateSelectorValues.Today] : {
      label         : translate('common:time.today'),
      daysFromToday : 0
    },
    [DateSelectorValues.Yesterday] : {
      label         : translate('common:time.yesterday'),
      daysFromToday : 1
    },
    [DateSelectorValues.LastSeven] : {
      label         : translate('common:time.lastDays', {number: 7}),
      daysFromToday : 7
    },
    [DateSelectorValues.LastThirty] : {
      label         : translate('common:time.lastDays', {number: 30}),
      daysFromToday : 30
    },
    [DateSelectorValues.Custom] : {
      label         : translate('common:time.custom'),
      daysFromToday : null,
      icon          : <ChevronRight />,
      onClick       : function() {
        setCustomDateMode(true);
        setSelectedRadio(DateSelectorValues.Custom);
      }
    }
  };

  const handleSubmit = function() {
    const value = isCustomValue(selectedRadio) ? (selectedDate || DateSelectorValues.LastSeven) : selectedRadio;
    const {fromDate, toDate} = getDateRangeFromValue(value);
    const dateRange: DateRangeType = {
      startDate : fromDate.toISOString(),
      endDate   : toDate.toISOString()
    };
    setSelectedRange(dateRange);
    onSubmit({
      notificationType,
      selectedRadio,
      dateRange
    });
  };

  return (
    <Dialog open={open}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={classes.formTitle}>
          <div onClick={onCancel} className={classes.closeButton}>
            <MaterialExitCloseIcon size={IconSize.medium}
              color={dlsColor('grey3')} />
          </div>
          <Typography component='h1' variant='h2'>
            {translate('common:editors.shared.filterTitle')}
          </Typography>
        </div>
      </DialogTitle>
      <div className={customDateMode ? classes.formWrapperFilter : classes.formWrapper}>
        <DialogContent className={classes.formDialogContent}>
          <>
            <DateFilterTitle isNotificationFilter
              customDateMode={customDateMode}
              onBackClick={function() {
                setCustomDateMode(false);
              }} />
            {!customDateMode &&
             <>
               <Typography component='h4' variant='h4'>
                 {translate('common:editors.shared.dates')}
               </Typography><Typography component='h6' className={classes.dateHeader}>
                 {translate('common:editors.shared.dateHeader')}
               </Typography>
             </>}
            <DateFilter isNotificationFilter
              customDateMode={customDateMode}
              onRadioChange={onRadioChange}
              selectedRadio={selectedRadio}
              setSelectedDate={setSelectedDate}
              radioMap={radioMap}
              setDateError={setDateError} />
            <Typography component='h4' variant='h4'>
              {translate('common:editors.shared.notificationType')}
            </Typography>
            <Typography component='h6' className={classes.dateHeader}>
              {translate('common:editors.shared.notificationTypeHeader')}
            </Typography>
            <FormItem>
              <NotificationTypeChooser isCtcAdmin
                readOnly={loading}
                defaultLabel={false}
                accountId={entityId(account)}
                onChange={function(value) {
                  setNotificationType(value.name);
                }}
                defaultValue={notificationType} />
            </FormItem>
          </>
        </DialogContent>
      </div>
      <DialogActions className={classes.formActions}>
        <TertiaryButton disabled={loading}
          onClick={function(e) {
            onCancel();
          }}>
          {translate('common:editors.shared.cancel')}
        </TertiaryButton>
        <div className={classes.primaryButton}>
          <PrimaryButton type='submit'
            onClick={handleSubmit}
            disabled={loading}
            fullWidth >
            {!loading ? <>{translate('common:editors.shared.filter')}</> : <ButtonSpinner />}
          </PrimaryButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationFilters;
